import { useState } from 'react';
import { Link } from 'react-router';
import { getRegionCounts } from '@shared/api/adminApi.ts';
import { Alert, Button } from '@shared/components/Button.tsx';
import { Icon } from '@shared/components/Icon.tsx';
import { Loading, Skeleton } from '@shared/components/Skeleton.tsx';
import { useToast } from '@shared/components/Toast.tsx';
import { districtLabel, REGIONS, typeShort } from '@shared/lib/enums.ts';
import { formatDateTimeAdmin, formatNumberAdmin } from '@shared/lib/format.ts';
import { useAsync } from '@shared/lib/useAsync.ts';
import type { ExportRange } from '@shared/types.ts';
import { AdminBanner } from '../Layout.tsx';
import { countSince, downloadExport, EXPORTS, fetchCandidates } from '../data.ts';

const DAY = 86400e3;

export function DashboardPage() {
  const list = useAsync(() => fetchCandidates(true), []);
  const regions = useAsync(getRegionCounts, []);
  const all = list.data ?? [];

  const tiles = [
    { label: 'एकूण नोंदणी', value: all.length, strong: true },
    { label: 'गेल्या २४ तासांत', value: countSince(all, DAY) },
    { label: 'गेल्या ७ दिवसांत', value: countSince(all, 7 * DAY) },
  ];
  const n = (t: string) => all.filter((c) => c.candidate_type === t).length;
  const school = n('school');
  const college = n('college');
  const working = n('open');
  const recent = [...all].sort((a, b) => b.created_at.localeCompare(a.created_at)).slice(0, 8);

  return (
    <>
      <AdminBanner title='डॅशबोर्ड' sub='नोंदणीचा सारांश, विभागनिहाय संख्या व एक्सपोर्ट' />
      <div className='admin-container admin-content'>
        {list.status === 'error' && (
          <Alert tone='error' title='स्पर्धकांची माहिती लोड होऊ शकली नाही'
            action={<Button size='sm' variant='secondary' icon='refresh' onClick={list.reload}>पुन्हा प्रयत्न करा</Button>} />
        )}

        {/* ---- headline numbers ---- */}
        <div className='tiles'>
          {tiles.map((t) => (
            <div key={t.label} className={`tile ${t.strong ? 'tile--strong' : ''}`}>
              <span className='tile__label'>{t.label}</span>
              {list.status === 'loading' && !list.data
                ? <Skeleton w={90} h={38} style={{ marginTop: 6 }} />
                : <span className='tile__value num'>{formatNumberAdmin(t.value)}</span>}
            </div>
          ))}
          <div className='tile tile--split'>
            <span className='tile__label'>गटनिहाय</span>
            {list.status === 'loading' && !list.data ? <Skeleton h={38} style={{ marginTop: 6 }} /> : (
              <ul className='split'>
                <li>
                  <span className='chip chip-school'>शालेय</span>
                  <strong className='num'>{formatNumberAdmin(school)}</strong>
                </li>
                <li>
                  <span className='chip chip-open'>खुला</span>
                  <strong className='num'>{formatNumberAdmin(college + working)}</strong>
                  <span className='split__sub num'>विद्यार्थी {formatNumberAdmin(college)} · व्यावसायिक {formatNumberAdmin(working)}</span>
                </li>
              </ul>
            )}
          </div>
        </div>

        <div className='dash-grid'>
          {/* ---- per region ---- */}
          <section className='panel'>
            <header className='panel__head'>
              <h2>विभागनिहाय स्पर्धक</h2>
              <Button size='sm' variant='ghost' icon='refresh' onClick={regions.reload} aria-label='पुन्हा लोड करा'
                loading={regions.status === 'loading' && !!regions.data}>अद्ययावत</Button>
            </header>
            {regions.status === 'error' && !regions.data ? (
              <Alert tone='error' title='विभागनिहाय संख्या लोड होऊ शकली नाही'
                action={<Button size='sm' variant='secondary' onClick={regions.reload}>पुन्हा</Button>} />
            ) : !regions.data ? <RegionSkeleton /> : <RegionBars counts={regions.data} />}
          </section>

          {/* ---- export ---- */}
          <section className='panel'>
            <header className='panel__head'>
              <h2>स्पर्धक यादी एक्सपोर्ट (Excel)</h2>
            </header>
            <ExportButtons />
          </section>
        </div>

        {/* ---- recent ---- */}
        <section className='panel'>
          <header className='panel__head'>
            <h2>अलीकडील नोंदणी</h2>
            <Link to='/candidates' className='btn btn-secondary btn-sm'>सर्व स्पर्धक →</Link>
          </header>
          {list.status === 'loading' && !list.data ? (
            <Loading>{Array.from({ length: 5 }, (_, i) => <Skeleton key={i} h={42} style={{ marginBottom: 8 }} />)}</Loading>
          ) : recent.length === 0 ? <p className='muted'>अद्याप एकही नोंदणी झालेली नाही.</p> : (
            <ul className='recent'>
              {recent.map((c) => (
                <li key={c.id}>
                  <Link to={`/candidates?view=${c.id}`} className='recent__name'>{c.name}</Link>
                  <span className={`chip chip-${c.candidate_type}`}>{typeShort(c.candidate_type)}</span>
                  <span className='muted recent__place'>{c.city}, {districtLabel(c.district)}</span>
                  <span className='muted num recent__time'>{formatDateTimeAdmin(c.created_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </>
  );
}

function RegionBars({ counts }: { counts: Record<string, number | undefined> }) {
  // Regions with zero candidates are absent from the API response — show them as 0.
  const rows = REGIONS.map((r) => ({ ...r, n: counts[r.value] ?? 0 })).sort((a, b) => b.n - a.n);
  const max = Math.max(1, ...rows.map((r) => r.n));
  const total = rows.reduce((s, r) => s + r.n, 0);
  const [showTable, setShowTable] = useState(false);

  return (
    <>
      {!showTable ? (
        <ul className='bars' aria-label='विभागनिहाय स्पर्धक संख्या'>
          {rows.map((r) => (
            <li key={r.value} className='bar' title={`${r.label}: ${r.n} (${total ? Math.round((r.n / total) * 100) : 0}%)`}>
              <span className='bar__label'>{r.label}</span>
              <span className='bar__track'>
                <span className='bar__fill' style={{ width: `${(r.n / max) * 100}%` }} />
              </span>
              <span className='bar__value num'>{formatNumberAdmin(r.n)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <table className='mini-table'>
          <thead><tr><th>विभाग</th><th className='num'>स्पर्धक</th><th className='num'>टक्के</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.value}>
                <td>{r.label}</td>
                <td className='num'>{formatNumberAdmin(r.n)}</td>
                <td className='num'>{total ? ((r.n / total) * 100).toFixed(1) : '0.0'}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <button type='button' className='link-btn small' onClick={() => setShowTable((v) => !v)}>
        {showTable ? 'आलेख दाखवा' : 'तक्ता दाखवा'}
      </button>
    </>
  );
}

function RegionSkeleton() {
  return (
    <Loading label='विभागनिहाय संख्या लोड होत आहे…'>
      <ul className='bars'>
        {[90, 72, 60, 44, 30, 20].map((w, i) => (
          <li key={i} className='bar'>
            <Skeleton w='80%' h={14} />
            <span className='bar__track'><Skeleton w={`${w}%`} h={14} /></span>
            <Skeleton w={28} h={14} />
          </li>
        ))}
      </ul>
    </Loading>
  );
}

export function ExportButtons() {
  const toast = useToast();
  const [busy, setBusy] = useState<ExportRange | null>(null);
  const run = async (r: ExportRange) => {
    setBusy(r);
    try {
      await downloadExport(r);
      toast('success', import.meta.env.VITE_USE_MOCK === 'true'
        ? 'डेमो: खऱ्या सर्व्हरवर येथे Excel (.xlsx) फाइल डाउनलोड होईल.'
        : 'फाइल डाउनलोड झाली.');
    } catch {
      toast('error', 'एक्सपोर्ट अयशस्वी झाला. पुन्हा प्रयत्न करा.');
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className='exports'>
      {EXPORTS.map((e) => (
        <button key={e.range} type='button' className='export' onClick={() => run(e.range)} disabled={busy !== null}
          aria-busy={busy === e.range || undefined}>
          <span className='export__icon'>{busy === e.range ? <span className='spinner' /> : <Icon name='download' size={20} />}</span>
          <span className='export__text'>
            <strong>{e.label}</strong>
            <span>{busy === e.range ? 'फाइल तयार होत आहे…' : e.hint}</span>
          </span>
        </button>
      ))}
    </div>
  );
}
